import { Auth } from 'aws-amplify';
import appConfig from '../config/app-config';

const API = appConfig.apis.monitoringApiEnv;
const baseUrl = API.address;

// ============================================================
// Types
// ============================================================

export interface Classification {
    id: string;
    person_id: string;
    person_name?: string;
    tax_id?: string;
    risk_level: 'EXTREME' | 'HIGH' | 'MEDIUM' | 'LOW';
    active: boolean;
    first_analysis_at: string | null;
    last_analysis_at: string | null;
    next_monitoring_at: string | null;
    notes?: string;
    created_by: string;
    created_at: string;
}

export interface Divergence {
    field: string;
    previous_value: unknown;
    current_value: unknown;
}

export interface ExecutionResult {
    id: string;
    batch_id: string;
    person_id: string;
    tax_id: string;
    risk_level: string;
    last_analyzed_at: string | null;
    is_baseline: boolean;
    divergences: Divergence[];
}

export interface HistoryItem {
    id: string;
    document: string;
    domain: string;
    operation_type: string;
    operation_result: string;
    details: Record<string, unknown>;
    user_id: string | null;
    created_at: string;
}

export interface DivergenceRecord {
    id: string;
    document: string;
    divergences: Divergence[];
    provider_data: Record<string, unknown>;
    created_at: string;
}

export interface PaginatedResponse<T> {
    data: {
        items: T[];
        total: number;
        page: number;
        pageSize: number;
        totalPages?: number;
    };
    meta: Record<string, string>;
}

export interface SingleResponse<T> {
    data: T;
    meta: Record<string, string>;
}

// ============================================================
// DTOs (Request)
// ============================================================

export interface CreateMonitoringDTO {
    person_id: string;
    tax_id: string;
    person_name: string;
    risk_level: 'EXTREME' | 'HIGH' | 'MEDIUM' | 'LOW';
    notes?: string;
}

export interface UpdateRiskLevelDTO {
    risk_level: 'EXTREME' | 'HIGH' | 'MEDIUM' | 'LOW';
    notes: string;
}

export interface DeactivateDTO {
    notes?: string;
}

export interface ExecuteMonitoringDTO {
    person_id: string;
    provider_data: Record<string, unknown>;
    recommendation_data?: Record<string, unknown>;
    notes?: string;
}

export interface ListParams {
    page?: number;
    pageSize?: number;
    riskLevel?: string;
    active?: boolean;
    search?: string;
    personName?: string;
}

export interface HistoryParams {
    page?: number;
    pageSize?: number;
    document?: string;
    dateFrom?: string;
    dateTo?: string;
    hasDivergence?: boolean;
}

export interface DivergenceParams {
    page?: number;
    pageSize?: number;
    dateFrom?: string;
    dateTo?: string;
}

// ============================================================
// HTTP Helpers
// ============================================================

const getHeaders = async (): Promise<Record<string, string>> => {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    return {
        'Content-Type': 'application/json',
        'x-api-key': API.key,
        'Authorization': `Bearer ${token}`,
    };
};

const handleResponse = async (res: Response) => {
    if (res.ok) return res.json();
    const error = await res.json().catch(() => ({ errors: [{ message: `Erro ${res.status}` }] }));
    throw new MonitoringApiError(res.status, error?.errors?.[0]?.message || `API error: ${res.status}`);
};

const get = async (path: string) =>
    handleResponse(await fetch(`${baseUrl}${path}`, { headers: await getHeaders() }));

const post = async (path: string, data: Record<string, unknown>) =>
    handleResponse(await fetch(`${baseUrl}${path}`, { method: 'POST', headers: await getHeaders(), body: JSON.stringify(data) }));

const put = async (path: string, data: Record<string, unknown>) =>
    handleResponse(await fetch(`${baseUrl}${path}`, { method: 'PUT', headers: await getHeaders(), body: JSON.stringify(data) }));

const qs = (params: Record<string, unknown> | object) => {
    const s = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v != null && v !== '') s.set(k, String(v));
    });
    const str = s.toString();
    return str ? `?${str}` : '';
};

// ============================================================
// Error class
// ============================================================

export class MonitoringApiError extends Error {
    constructor(public status: number, message: string) {
        super(message);
        this.name = 'MonitoringApiError';
    }
}

// ============================================================
// API Client
// ============================================================

export const monitoringApi = {
    // Classificações
    create: (data: CreateMonitoringDTO) =>
        post('/classifications', data as unknown as Record<string, unknown>),

    list: (params?: ListParams) =>
        get(`/page-list${qs(params || {})}`),

    updateRiskLevel: (personId: string, data: UpdateRiskLevelDTO) =>
        put(`/${personId}/risk-level`, data as unknown as Record<string, unknown>),

    deactivate: (personId: string, data: DeactivateDTO) =>
        put(`/${personId}/deactivate`, data as unknown as Record<string, unknown>),

    // Execução
    execute: (batchId: string, data: ExecuteMonitoringDTO) =>
        post(`/batch/${batchId}/execute`, data as unknown as Record<string, unknown>),

    getPending: (params?: { page?: number; pageSize?: number }) =>
        get(`/scan/pending${qs(params || {})}`),

    // Histórico
    getHistory: (params?: HistoryParams) =>
        get(`/history/page-list${qs(params || {})}`),

    // Divergências
    getDivergences: (personId: string, params?: DivergenceParams) =>
        get(`/${personId}/divergences${qs(params || {})}`),
};
